/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
/* eslint-disable object-curly-newline */
import React, {
  useContext, useMemo, useState
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { Input, CardInfo, Typography, Divider } from '@telus/koodo-ui-kit'
import { PAYMENT_STATE, updatePaymentStates } from '@apps/redux/features/PayAndReviewSlice'
import { PAYMENT_OPTIONS, ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { getById, getBySlug } from '@apps/utils/contentful'
import { getPromoNames } from '@apps/utils/common'
import CreditCardSection from './CreditCardSection'
import VoucherOptionSection from './VoucherOptionSection'
import TermsAndConditions from './TermsAndConditions'
import Sites from '@/sites'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'
import MarkdownRenderer from '@/apps/components/MarkdownRenderer'

const PaymentOption = ({
  showErrors, onPaymentOptionChange, handleSIMActivation, textContent, setButtonIsLoading, promoCodeSlot
}) => {
  const dispatch = useDispatch()
  const paymentState = useSelector((state) => state.payAndReviewPage)
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(PAYMENT_OPTIONS.CREDIT_CARD)

  const {
    content: { pageContent },
    user: { selectedLanguage }
  } = useSelector((state) => state.common)

  const { userType } = useSelector((state) => state.common)
  const pagePrefix = userType === Sites.dealer ? 'Dealer' : 'Consumer'

  const location = useLocation()
  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region
  } = useContext(ApplicationContext)

  const tagManagerInfo = useTagManagerInfo()

  // eslint-disable-next-line camelcase
  const pushClickAnalytics = ({ click_text, promoCodeDescription, event_type, target_link, event_click = ANALYTICS_EVENT_TYPES.CLICK } = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, {
        ...tagManagerInfo,
        click_text,
        promoCodeDescription
      }, event_type, event_click, target_link)
    )
  }

  const content = useMemo(() => {
    const paymentPage = getById(pageContent, `paymentPage${pagePrefix}`)?.[selectedLanguage]
    const { shortTextFields, longTextFields } = paymentPage || {}

    return {
      validCreditCardContent: getBySlug(longTextFields, 'validCreditCardContent')?.value,
      atuContentTitle: getBySlug(shortTextFields, 'atuContentTitle')?.value,
      atuContentBody: getBySlug(longTextFields, 'atuContentBody')?.value
    }
  }, [pageContent, selectedLanguage, pagePrefix])

  const {
    [PAYMENT_STATE.PAYMENT_GATEWAY]: paymentGateway,
    [PAYMENT_STATE.TERMS_OF_SERVICE]: isTermsConditionsChecked,
    [PAYMENT_STATE.PROMO.MULTI_PROMOTION_CODE_INFO]: multiPromotionCodeInfo,
  } = paymentState

  const updatePaymentStore = (payload) => {
    dispatch(updatePaymentStates(payload))
  }

  const onNumberOptionSelect = (name) => {
    if (name === 'creditCard') {
      pushClickAnalytics({ click_text: 'Credit card pay click', promoCodeDescription: getPromoNames(multiPromotionCodeInfo), event_type: textContent?.shortTextFields?.payWithCardLabel })
    } else {
      pushClickAnalytics({ click_text: 'Voucher pay click', promoCodeDescription: getPromoNames(multiPromotionCodeInfo), event_type: textContent?.shortTextFields?.payWithVoucher })
    }
    setSelectedPaymentOption(name)
    // Clear the page-level error flag so validation errors shown for the
    // previous payment mode don't carry over to the newly selected one.
    onPaymentOptionChange()
    updatePaymentStore({
      [PAYMENT_STATE.SELECTED_PAYMENT_OPTION]: name,
      [PAYMENT_STATE.TRIGGER_EPS_CARD_VALIDATION]: false
    })
  }

  const handleTermsOfCondition = () => {
    pushClickAnalytics({ click_text: textContent?.shortTextFields?.termsAgreement, promoCodeDescription: getPromoNames(multiPromotionCodeInfo), event_type: 'Terms and Aggrement click' })
    updatePaymentStore({ [PAYMENT_STATE.TERMS_OF_SERVICE]: !isTermsConditionsChecked })
    // setIsTermsConditionsChecked(!isTermsConditionsChecked)
    // dispatch(setPaymentInformation({
    //   ...paymentInfo,
    //   termsOfService: !isTermsConditionsChecked
    // }))
  }

  return (
    <div className="space-y-6">
      <div>
        <Typography variant={{ size: 'h3' }} >{textContent?.shortTextFields?.paymentOptionsHeading}</Typography>
      </div>
      <div>
        <CardInfo tokens={{ backgroundColor: 'var(--color-brand-purple-light)' }}>
          <MarkdownRenderer
            markdown={content.atuContentBody}
            paragraphClassForIndex={() => 'text-copy text-xs font-body leading-5'}
          />
        </CardInfo>
      </div>
      <div className="space-y-5">
        <div className="-mt-2">
          <Input
            type="radio"
            options={[
              { title: textContent?.shortTextFields?.payWithCardLabel, value: PAYMENT_OPTIONS.CREDIT_CARD },
              { title: textContent?.shortTextFields?.payWithVoucher, value: PAYMENT_OPTIONS.VOUCHER },
            ]}
            value={selectedPaymentOption}
            onChange={(value) => onNumberOptionSelect(value)}
          />
        </div>
        <div>
          <Divider />
        </div>
        {selectedPaymentOption === PAYMENT_OPTIONS.CREDIT_CARD && (
          <CreditCardSection
            textContent={textContent}
            paymentGateway={paymentGateway}
            handleSIMActivation={handleSIMActivation}
            showErrors={showErrors}
            setButtonIsLoading={setButtonIsLoading}
            validCreditCardContent={content.validCreditCardContent}
          />
        )}
        {selectedPaymentOption === PAYMENT_OPTIONS.VOUCHER && (
          <VoucherOptionSection
            handleSIMActivation={handleSIMActivation}
            showErrors={showErrors}
            textContent={textContent}
            setButtonIsLoading={setButtonIsLoading}
          />
        )}
        <div>
          <Divider />
        </div>
        {promoCodeSlot}
        <div>
          <Divider />
        </div>
        <TermsAndConditions
          textContent={textContent}
          showErrors={showErrors}
          isTermsConditionsChecked={isTermsConditionsChecked}
          onToggle={handleTermsOfCondition}
          pushClickAnalytics={(args) => pushClickAnalytics({ ...args, promoCodeDescription: getPromoNames(multiPromotionCodeInfo) })}
        />
      </div>
    </div>
  )
}

export default PaymentOption
